import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  message,
  Space,
  Divider,
  List,
  Tag,
  Modal,
  Popconfirm,
  Tooltip,
  InputNumber,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CarOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { vipService, VIPParking, VIPVehicle, ParkingSpot } from '../../../services/vipService';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface VIPFormProps {
  initialData?: VIPParking;
  onSuccess: () => void;
  onCancel?: () => void;
}

interface VehicleFormData {
  licensePlate: string;
  brand: string;
  model: string;
  color: string;
}

// 本地車輛接口，包含前端管理需要的_id
interface LocalVIPVehicle extends VIPVehicle {
  _id: string;
}

const VIPForm: React.FC<VIPFormProps> = ({ initialData, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableSpots, setAvailableSpots] = useState<ParkingSpot[]>([]);
  const [vehicles, setVehicles] = useState<LocalVIPVehicle[]>([]);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<LocalVIPVehicle | null>(null);
  const [vehicleForm] = Form.useForm();

  useEffect(() => {
    loadAvailableSpots();
    if (initialData) {
      // 編輯模式，填充表單數據
      form.setFieldsValue({
        parkingSpotId: initialData.parkingSpot._id,
        ownerName: initialData.owner.name,
        ownerPhone: initialData.owner.phone,
        ownerEmail: initialData.owner.email,
        vipLevel: initialData.vipLevel,
        dateRange: [dayjs(initialData.validFrom), dayjs(initialData.validTo)],
        status: initialData.status,
      });
      const safeVehicles = Array.isArray(initialData.vehicles) ? initialData.vehicles : [];
      setVehicles(safeVehicles.map((v, index) => ({
        ...v,
        _id: `vehicle-${index}-${Date.now()}`
      })));
    }
  }, [initialData, form]);

  const loadAvailableSpots = async () => {
    try {
      const response = await vipService.getAvailableSpots();
      const spots = Array.isArray(response?.data) ? response.data : [];
      setAvailableSpots(spots);
    } catch (error) {
      console.error('載入可用車位失敗:', error);
      message.error('載入可用車位失敗');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (initialData) {
        // 編輯模式
        const updateData = {
          owner: {
            name: values.ownerName,
            phone: values.ownerPhone,
            email: values.ownerEmail,
          },
          vipLevel: values.vipLevel,
          validFrom: values.dateRange[0].format('YYYY-MM-DD'),
          validTo: values.dateRange[1].format('YYYY-MM-DD'),
          status: values.status || 'active',
          privileges: values.privileges || [],
          vehicles: vehicles.map(v => ({
            licensePlate: v.licensePlate,
            brand: v.brand,
            model: v.model,
            color: v.color,
            isActive: v.isActive !== undefined ? v.isActive : true,
            addedAt: v.addedAt || new Date().toISOString(),
          })),
        };
        await vipService.updateVIPParking(initialData._id, updateData);
        message.success('VIP車位更新成功');
      } else {
        // 新增模式
        const createData = {
          parkingSpotId: values.parkingSpotId,
          owner: {
            name: values.ownerName,
            phone: values.ownerPhone,
            email: values.ownerEmail,
          },
          vipLevel: values.vipLevel,
          validFrom: values.dateRange[0].format('YYYY-MM-DD'),
          validTo: values.dateRange[1].format('YYYY-MM-DD'),
          privileges: values.privileges || [],
          vehicles: vehicles.map(v => ({
            licensePlate: v.licensePlate,
            brand: v.brand,
            model: v.model,
            color: v.color,
          })),
        };
        await vipService.createVIPParking(createData);
        message.success('VIP車位創建成功');
      }

      onSuccess();
      if (!initialData) {
        // 新增模式下重置表單
        form.resetFields();
        setVehicles([]);
      }
    } catch (error: any) {
      console.error('保存VIP車位失敗:', error);
      message.error(error.response?.data?.message || '保存VIP車位失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    vehicleForm.resetFields();
    setVehicleModalVisible(true);
  };

  const handleEditVehicle = (vehicle: LocalVIPVehicle) => {
    setEditingVehicle(vehicle);
    vehicleForm.setFieldsValue({
      licensePlate: vehicle.licensePlate,
      brand: vehicle.brand,
      model: vehicle.model,
      color: vehicle.color,
    });
    setVehicleModalVisible(true);
  };

  const handleVehicleSubmit = (values: VehicleFormData) => {
    const newVehicle: LocalVIPVehicle = {
      _id: editingVehicle?._id || Date.now().toString(),
      licensePlate: values.licensePlate,
      brand: values.brand,
      model: values.model,
      color: values.color,
      isActive: editingVehicle?.isActive ?? true,
      addedAt: editingVehicle?.addedAt || new Date().toISOString(),
    };

    if (editingVehicle) {
      // 編輯車輛
      setVehicles(prev => prev.map(v => v._id === editingVehicle._id ? newVehicle : v));
    } else {
      // 新增車輛
      setVehicles(prev => [...prev, newVehicle]);
    }

    setVehicleModalVisible(false);
    vehicleForm.resetFields();
    setEditingVehicle(null);
  };

  const handleDeleteVehicle = (vehicleId: string) => {
    setVehicles(prev => prev.filter(v => v._id !== vehicleId));
  };

  const handleToggleVehicleStatus = (vehicleId: string) => {
    setVehicles(prev => prev.map(v => 
      v._id === vehicleId ? { ...v, isActive: !v.isActive } : v
    ));
  };

  return (
    <div className="vip-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          vipLevel: 'silver',
          status: 'active',
        }}
      >
        <Card title="基本信息" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="parkingSpotId"
                label="選擇車位"
                rules={[{ required: true, message: '請選擇車位' }]}
              >
                <Select
                  placeholder="請選擇可用車位"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {availableSpots.map(spot => (
                    <Option key={spot._id} value={spot._id}>
                      {spot.spotNumber} - {spot.floor}樓 {spot.zone}區
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="vipLevel"
                label="VIP等級"
                rules={[{ required: true, message: '請選擇VIP等級' }]}
              >
                <Select placeholder="請選擇VIP等級">
                  <Option value="silver">銀牌會員</Option>
                  <Option value="gold">黃金會員</Option>
                  <Option value="platinum">白金會員</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="status"
                label="狀態"
                rules={[{ required: true, message: '請選擇狀態' }]}
              >
                <Select placeholder="請選擇狀態">
                  <Option value="active">啟用中</Option>
                  <Option value="suspended">已暫停</Option>
                  <Option value="expired">已過期</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="dateRange"
                label="有效期"
                rules={[{ required: true, message: '請選擇有效期' }]}
              >
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['開始日期', '結束日期']}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="會員信息" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="ownerName"
                label="會員姓名"
                rules={[{ required: true, message: '請輸入會員姓名' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="請輸入會員姓名"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="ownerPhone"
                label="聯繫電話"
                rules={[
                  { required: true, message: '請輸入聯繫電話' },
                  { pattern: /^1[3-9]\d{9}$/, message: '請輸入正確的手機號碼' },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="請輸入聯繫電話"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="ownerEmail"
                label="電子郵箱"
                rules={[
                  { required: true, message: '請輸入電子郵箱' },
                  { type: 'email', message: '請輸入正確的郵箱格式' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="請輸入電子郵箱"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card 
          title="車輛信息" 
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddVehicle}
            >
              添加車輛
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          <List
            dataSource={vehicles}
            locale={{ emptyText: '暫無車輛信息，請點擊上方按鈕添加車輛' }}
            renderItem={(vehicle) => (
              <List.Item
                actions={[
                  <Tooltip title={vehicle.isActive ? '停用' : '啟用'}>
                    <Button
                      type="text"
                      onClick={() => handleToggleVehicleStatus(vehicle._id)}
                    >
                      <Tag color={vehicle.isActive ? 'green' : 'red'}>
                        {vehicle.isActive ? '啟用' : '停用'}
                      </Tag>
                    </Button>
                  </Tooltip>,
                  <Tooltip title="編輯">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditVehicle(vehicle)}
                    />
                  </Tooltip>,
                  <Popconfirm
                    title="確定要刪除這輛車嗎？"
                    onConfirm={() => handleDeleteVehicle(vehicle._id)}
                    okText="確定"
                    cancelText="取消"
                  >
                    <Tooltip title="刪除">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={<CarOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                  title={
                    <Space>
                      <span style={{ fontWeight: 'bold' }}>{vehicle.licensePlate}</span>
                      <Tag color={vehicle.isActive ? 'green' : 'red'}>
                        {vehicle.isActive ? '啟用' : '停用'}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <div>品牌型號：{vehicle.brand} {vehicle.model}</div>
                      <div>顏色：{vehicle.color}</div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        添加時間：{dayjs(vehicle.addedAt).format('YYYY-MM-DD HH:mm')}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        <Card>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
            >
              {initialData ? '更新' : '創建'}VIP車位
            </Button>
            {onCancel && (
              <Button
                onClick={onCancel}
                icon={<CloseOutlined />}
              >
                取消
              </Button>
            )}
          </Space>
        </Card>
      </Form>

      {/* 車輛信息模態框 */}
      <Modal
        title={editingVehicle ? '編輯車輛信息' : '添加車輛信息'}
        open={vehicleModalVisible}
        onCancel={() => setVehicleModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={vehicleForm}
          layout="vertical"
          onFinish={handleVehicleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="licensePlate"
                label="車牌號碼"
                rules={[{ required: true, message: '請輸入車牌號碼' }]}
              >
                <Input
                  placeholder="請輸入車牌號碼"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="brand"
                label="車輛品牌"
                rules={[{ required: true, message: '請輸入車輛品牌' }]}
              >
                <Input placeholder="請輸入車輛品牌" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="model"
                label="車輛型號"
                rules={[{ required: true, message: '請輸入車輛型號' }]}
              >
                <Input placeholder="請輸入車輛型號" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="color"
                label="車輛顏色"
                rules={[{ required: true, message: '請輸入車輛顏色' }]}
              >
                <Select placeholder="請選擇車輛顏色">
                  <Option value="白色">白色</Option>
                  <Option value="黑色">黑色</Option>
                  <Option value="銀色">銀色</Option>
                  <Option value="灰色">灰色</Option>
                  <Option value="紅色">紅色</Option>
                  <Option value="藍色">藍色</Option>
                  <Option value="綠色">綠色</Option>
                  <Option value="黃色">黃色</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingVehicle ? '更新' : '添加'}車輛
              </Button>
              <Button onClick={() => setVehicleModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VIPForm;